/* eslint-disable react/display-name */
import React from 'react';
import styled, { css } from 'styled-components';

import { ModalPosition } from './index';
import { SidebarWidth } from '../Sidebar';
import { fadeIn } from 'ui/theme/keyframes';
import { CrossIcon } from 'ui/components/styled';

const Overlay = styled.div`
  display: ${({ show }) => (show ? 'block' : 'none')};
  z-index: ${({ zIndex = 999 }) => zIndex};
  animation: ${fadeIn} 100ms ease-out;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgba(55, 81, 126, 0.3);
  overflow: auto;
`;

export const ContainerStyle = css`
  position: absolute;
  border-radius: 8px;
  background-color: ${({ bg, theme }) => bg || theme.colors.white};
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
  width: ${({ width = '33%' }) => width};
  max-width: ${({ maxWidth = 'initial' }) => maxWidth};
  ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
  height: auto;
  padding: ${({ padding = '1.3rem 1.5rem 2rem' }) => padding};

  top: ${({ position, top }) =>
    ({
      [ModalPosition.TOP_LEFT]: top || '10%',
      [ModalPosition.TOP_CENTER]: top || '10%',
      [ModalPosition.TOP_RIGHT]: top || '10%',
      [ModalPosition.CENTER_CENTER]: '50%',
    }[position])};

  left: ${({ position, adjustPosition = true }) =>
    ({
      [ModalPosition.TOP_LEFT]: adjustPosition ? `calc(5% + ${SidebarWidth}px)` : '5%',
      [ModalPosition.TOP_CENTER]: adjustPosition ? `calc(50% + ${SidebarWidth / 2}px)` : '50%',
      [ModalPosition.TOP_RIGHT]: '95%',
      [ModalPosition.CENTER_CENTER]: adjustPosition ? `calc(50% + ${SidebarWidth / 2}px)` : '50%',
    }[position])};

  transform: ${({ position }) =>
    ({
      [ModalPosition.TOP_LEFT]: 'translate(0, 0)',
      [ModalPosition.TOP_CENTER]: 'translate(-50%, 0)',
      [ModalPosition.TOP_RIGHT]: 'translate(-100%, 0)',
      [ModalPosition.CENTER_CENTER]: 'translate(-50%, -50%)',
    }[position])};
`;

const Container = styled.div`
  ${ContainerStyle}
`;

const Header = styled.div`
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.04em;
  text-align: left;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin-bottom: 15px;
  > span {
    user-select: none;
  }
`;

const Content = styled.div`
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-align: left;
  color: ${({ theme }) => theme.colors.grey};
  ${({ userSelect }) => userSelect && `user-select: ${userSelect};`}
`;

export const Modal = React.forwardRef(
  (
    {
      show,
      title,
      children,
      position = ModalPosition.CENTER_CENTER,
      closeOnOutsideClick = true,
      closeOnEscKeyPress = true,
      zIndex,
      onClose,
      contentProps,
      stopClickPropagation = false,
      containerComponent,
      ...rest
    },
    initialRef
  ) => {
    const ref = initialRef || React.useRef();
    const closeAllowed = React.useRef(false);
    const ContainerComponent = containerComponent || Container;

    React.useEffect(() => {
      if (!closeOnEscKeyPress) return;

      document.addEventListener('keydown', handleKeyDown, false);

      return () => document.removeEventListener('keydown', handleKeyDown, false);
    }, []);

    React.useEffect(() => {
      if (show) {
        // prevent fast close
        closeAllowed.current = false;
        const handle = setTimeout(() => (closeAllowed.current = true), 500);
        return () => clearTimeout(handle);
      }
    }, [show]);

    const handleKeyDown = React.useCallback((event) => {
      // Esc key
      if (event.keyCode === 27) {
        handleClose();
      }
    }, []);

    const handleClose = () => {
      if (onClose) {
        onClose();
      }
    };

    const handleOutsideClick = (event) => {
      if (stopClickPropagation) {
        event.stopPropagation();
      }

      if (
        !closeOnOutsideClick ||
        !closeAllowed.current ||
        !ref.current ||
        ref.current.contains(event.target)
      ) {
        return;
      }

      handleClose();
    };

    return (
      <Overlay show={show} zIndex={zIndex} onMouseDown={handleOutsideClick}>
        <ContainerComponent ref={ref} position={position} {...rest}>
          {title && (
            <Header>
              <span>{title}</span>
              <CrossIcon top="-5px" right="-5px" onClick={handleClose} />
            </Header>
          )}
          <Content {...contentProps}>{children}</Content>
        </ContainerComponent>
      </Overlay>
    );
  }
);
